<script setup lang="ts">
//#region imports
import { computed } from 'vue';
import { useStore } from 'vuex';
import UiDropdownSelect from '@/views/CRM/components/UiDropdownSelect.vue';
import UiChevron from '@/views/CRM/components/UiChevron.vue';
import type { GetLanguagesResponse } from '@/types/openapi-ts';
//#endregion

//#region define
const props = defineProps({
  selected: {
    type: Array,
    default: () => [],
  },
  languages: {
    type: [Object, Array],
    default: () => null,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
});
const emit = defineEmits(['select']);
//#endregion

//#region composables
const store = useStore();
//#endregion

//#region computed
const computedLanguages = computed<GetLanguagesResponse['payload']>(() => {
  if (props.languages) {
    return props.languages;
  }
  return store.state.misc.languages || [];
});

const items = computed(() => {
  return computedLanguages.value.map((item) => ({
    param: item.code,
    label: `${item.name} (${item.name_native})`,
  }));
});
//#endregion

//#region methods
const handleSelect = (event: string) => {
  emit('select', event);
};
//#endregion
</script>

<template>
  <UiDropdownSelect
    :items="items"
    :search-placeholder="$t('crm.activities.actions.action_popup.search_languages_placeholder')"
    :not-found-message="$t('crm.activities.actions.action_params_popup.not_found')"
    :selected="selected"
    placement="bottom-start"
    menu-class="languages-select__menu"
    class="languages-select__menu"
    :debounce-timeout="200"
    :disabled="disabled"
    @select="handleSelect"
  >
    <template v-slot:activator="slotProps">
      <UiButton
        class="t:w-full"
        icon="globe"
        :filled="slotProps.active"
        :disabled="disabled"
      >
        {{ $t('crm.activities.actions.add_translation') }}
        <UiChevron
          class="languages-select__chevron"
          :rotate="!slotProps.active"
        />
      </UiButton>
    </template>
  </UiDropdownSelect>
</template>

<style lang="scss">
.languages-select {
  &__chevron {
    margin-left: 4px;
    line-height: 12.5px;
  }
  &__menu {
    width: 100%;
    .el-dropdown{
      width: 100%;
    }
  }
}
</style>
